<template>
    <vvo v-slot="v">
        <div class="detail-container">
            <!-- 1. 데이터 없음 표시 영역 -->
            <div class="detail-placeholder" v-if="!detailInfo">
                <span class="detail-notification-icon">
                    <i class="fas fa-external-link-alt"></i>
                </span>
                <span class="detail-notification-text">알람 기준을 선택하세요.</span>
            </div>

            <!-- 2.상세 정보 영역 -->
            <div class="detail-main" v-if="detailInfo">
                <!-- 2.1 상세 정보 헤더 영역 -->
                <div class="detail-header">
                    <div class="col detail-title">
                        <span class="title">{{ viewTitle }}</span>
                        <span class="badge title1" v-if="detailInfo.alarmGroup">{{ detailInfo.alarmGroup }}</span>
                        <span class="badge title2" v-if="detailInfo.ptAddr">{{ detailInfo.ptAddr }}</span>
                        <span class="badge others" v-if="detailInfo.promoteAlarmType">{{ promoteTypeLabel }}</span>
                        <span class="badge others" v-if="isNewMode">신규</span>
                    </div>
                    <div class="detail-control" v-if="!isNewMode && !readonly">
                        <b-form-checkbox switch v-model="isEditMode" size="lg" />
                    </div>
                </div>

                <!-- 2.2 상세 정보 컨텐츠 표시 영역 -->
                <div class="detail-content">
                    <BaseInfoPanel :detailInfo="detailInfo" :mode="panelMode" @test-alarm="onTestAlarm" />
                </div>
            </div>

            <!-- 3.하단 영역 -->
            <div class="detail-bottom">
                <div class="detail-bottom-info" v-if="detailInfo">
                    <span>{{ `생성일자: ${detailInfo.regDate ?? "-"}` }}</span>
                    <span>{{ `수정일자: ${detailInfo.modDate ?? "-"}` }}</span>
                </div>
                <div class="detail-bottom-buttons" v-if="detailInfo && !readonly">
                    <button
                        type="button"
                        class="btn btn-primary detail-btn"
                        v-if="isEditMode"
                        @click="onValidate(v, saveData)"
                    >
                        저장
                    </button>
                    <button type="button" class="btn btn-secondary detail-btn" v-if="detailInfo" @click="handleClose($event)">
                        닫기
                    </button>
                </div>
            </div>
        </div>
    </vvo>
</template>

<script>
import BaseInfoPanel from "./panels/BaseInfoPanel.vue";
import { buildRuleParams, confirmUiFromRuleParams, messageRowsFromRuleParams } from "./panels/ruleParamsUi.js";
import backEndApi from "@src/api/backEndApi";
import moment from "moment";
import * as popupMessages from "@src/consts/popupMessageConsts";

export default {
    components: {
        BaseInfoPanel,
    },
    props: {
        title: { type: String, default: "알람 기준" },
        alarmKey: { type: String, default: null },
        /** AlarmList 모달 등 조회 전용 */
        readonly: { type: Boolean, default: false },
        /** 하위 호환: AlarmList의 alarm 객체 */
        alarm: { type: Object, default: null },
    },
    data() {
        return {
            detailInfo: null,
            mode: "view",
            testVal: 0,
            popupMessages,
        };
    },
    computed: {
        isEditMode: {
            get() {
                return this.mode == "edit" || this.mode == "new";
            },
            set(value) {
                this.mode = value ? "edit" : "view";
            },
        },
        isNewMode() {
            return this.mode === "new";
        },
        isViewMode() {
            return this.mode === "view";
        },
        viewTitle() {
            return this.title;
        },
        panelMode() {
            return this.readonly ? "view" : this.mode;
        },
        promoteTypeLabel() {
            const v = this.detailInfo?.promoteAlarmType || "Event";
            const found = (this.$store.state.commonCodes?.alarmType || []).find((c) => c.value === v);
            if (found) return found.text;
            return v === "EquipFault" ? "설비 오류" : "이벤트";
        },
        resolvedAlarmKey() {
            if (this.alarmKey) return this.alarmKey;
            if (this.alarm && this.alarm.alarmGroup && this.alarm.ptAddr) {
                return `${this.alarm.alarmGroup}||${this.alarm.ptAddr}`;
            }
            return null;
        },
    },
    watch: {
        resolvedAlarmKey: {
            immediate: true,
            handler(val) {
                if (!val) {
                    if (this.mode !== "new") this.clearData();
                } else {
                    this.loadData(val);
                }
            },
        },
    },
    methods: {
        parseAlarmKey(key) {
            if (!key || typeof key !== "string") return null;
            const idx = key.indexOf("||");
            if (idx < 0) return null;
            return {
                alarmGroup: key.slice(0, idx),
                ptAddr: key.slice(idx + 2),
            };
        },
        formatDt(dt) {
            if (!dt) return null;
            if (typeof dt === "string" && dt.length >= 14 && !dt.includes("-")) {
                // yyyyMMddHHmmss
                return moment(dt, "YYYYMMDDHHmmss").format("YYYY-MM-DD HH:mm:ss");
            }
            if (dt.format) return dt.format("yyyy-MM-dd HH:mm:ss");
            return moment(dt).format("YYYY-MM-DD HH:mm:ss");
        },
        async notifyCancelEdit() {
            if (this.isEditMode) {
                const result = await this.alertConfirmWarning("편집중인 내용을 무시하고 종료할까요?");
                if (result.value) return true;
                else return false;
            }
            return true;
        },
        newData() {
            this.mode = "new";
            this.detailInfo = {
                alarmGroup: "",
                ptAddr: "",
                alarmType: "A0",
                promoteAlarmType: "Event",
                ll: null,
                l: 0,
                h: 0,
                hh: null,
                isUse: true,
                isHold: false,
                enabled: "Y",
                description: "",
                solution: "",
                testVal: 0,
                regDate: null,
                modDate: null,
                confirmMode: "immediate",
                confirmMinutes: 10,
                messageRows: [],
            };
            return this.detailInfo;
        },
        async loadData(key) {
            const parsed = this.parseAlarmKey(key || this.resolvedAlarmKey);
            if (!parsed) {
                this.clearData();
                return;
            }

            try {
                const result = await backEndApi.alarm.getAlarm(parsed.alarmGroup, parsed.ptAddr);
                if (this.$err(result.data)) {
                    this.alertNoti(popupMessages.COMMON_SEARCH_NO_DATA_POPUP_MESSAGE);
                    this.clearData();
                    return;
                }

                const data = { ...result.data };
                if (data.alarmType === "D0" && (data.hh === null || data.hh === undefined)) {
                    data.hh = 1;
                }
                if (!data.promoteAlarmType || !["Event", "EquipFault"].includes(data.promoteAlarmType)) {
                    data.promoteAlarmType = "Event";
                }
                data.enabled = data.isUse ? "Y" : "N";
                data.regDate = this.formatDt(data.regDt);
                data.modDate = this.formatDt(data.updDt);
                data.testVal = 0;
                data.orgAlarmGroup = data.alarmGroup;
                data.orgPtAddr = data.ptAddr;
                if (data.alarmType === "D0" || data.alarmType === "D1") {
                    const ui = confirmUiFromRuleParams(data.ruleParams);
                    data.confirmMode = ui.mode;
                    data.confirmMinutes = ui.minutes;
                }
                data.messageRows = messageRowsFromRuleParams(data.ruleParams);

                this.detailInfo = data;
                this.mode = "view";
            } catch (e) {
                console.error(e);
            }
        },
        clearData() {
            this.detailInfo = null;
            this.mode = "view";
            this.$forceUpdate();
        },
        normalizeBeforeSave() {
            this.detailInfo.isUse = this.detailInfo.enabled === "Y" || this.detailInfo.isUse === true;
            if (!["Event", "EquipFault"].includes(this.detailInfo.promoteAlarmType)) {
                this.detailInfo.promoteAlarmType = "Event";
            }
            if (this.detailInfo.alarmType === "D0") {
                this.detailInfo.hh = Number(this.detailInfo.hh) === 0 ? 0 : 1;
                this.detailInfo.l = this.detailInfo.l != null ? this.detailInfo.l : 0;
                this.detailInfo.h = this.detailInfo.h != null ? this.detailInfo.h : 0;
                this.detailInfo.ll = null;
            } else if (this.detailInfo.alarmType === "D1") {
                this.detailInfo.l = this.detailInfo.l != null ? this.detailInfo.l : 0;
                this.detailInfo.h = this.detailInfo.h != null ? this.detailInfo.h : 0;
                this.detailInfo.ll = null;
                this.detailInfo.hh = null;
            } else if (this.detailInfo.alarmType === "A0") {
                this.detailInfo.ll = null;
                this.detailInfo.hh = null;
            }
            const built = buildRuleParams(this.detailInfo.alarmType, {
                mode: this.detailInfo.confirmMode,
                minutes: this.detailInfo.confirmMinutes,
                messageRows: this.detailInfo.alarmType === "D1" ? this.detailInfo.messageRows : [],
            });
            if (!built.ok) {
                this.alertWarning(built.message);
                return false;
            }
            this.detailInfo.ruleParams = built.params;
            return true;
        },
        async saveData() {
            try {
                if (!this.normalizeBeforeSave()) return;

                if (this.isNewMode) {
                    const { alarmGroup, ptAddr } = this.detailInfo;
                    const dup = await backEndApi.alarm.isDuplicateAlarm(alarmGroup, ptAddr);
                    if (this.$err(dup.data)) return;
                    if (dup.data.isDup) {
                        this.alertNoti(popupMessages.COMMON_API_CALL_FAIL_MESSAGE);
                        return;
                    }

                    const result = await backEndApi.alarm.insertAlarm(this.detailInfo);
                    if (this.$err(result.data)) return;

                    await this.alertNoti(popupMessages.COMMON_SAVE_POPUP_MESSAGE);
                    this.mode = "view";
                    this.$emit("data-updated", "insert", { alarmGroup, ptAddr });
                    await this.loadData(`${alarmGroup}||${ptAddr}`);
                } else {
                    const { alarmGroup, ptAddr } = this.detailInfo;
                    const result = await backEndApi.alarm.updateAlarm(this.detailInfo);
                    if (this.$err(result.data)) return;

                    await this.alertNoti(popupMessages.COMMON_SAVE_POPUP_MESSAGE);
                    this.mode = "view";
                    this.$emit("data-updated", "update", { alarmGroup, ptAddr });
                    await this.loadData(`${alarmGroup}||${ptAddr}`);
                }
            } catch (e) {
                console.error(e);
            }
        },
        async handleClose(event) {
            const result = await this.notifyCancelEdit();
            if (!result) {
                event && event.preventDefault();
                return false;
            }
            this.clearData();
            this.$emit("closed", this.alarmKey);
            return true;
        },
        onTestAlarm(testVal) {
            this.alertQuestion(popupMessages.ALARM_TEST_POPUP_MESSAGE).then((result) => {
                if (!result.value) return;

                const { alarmGroup, ptAddr } = this.detailInfo;
                backEndApi.alarm
                    .insertAlarmTest(alarmGroup, ptAddr, testVal)
                    .then((res) => {
                        const data = res && res.data;
                        if (this.$err(data, () => {})) {
                            const msg =
                                (data && data.message) ||
                                "알람 테스트에 실패했습니다. 알람이 발생하는 테스트값을 입력해 주세요.";
                            this.alertWarning(msg);
                            return;
                        }
                        this.alertNoti(popupMessages.ALARM_TEST_SUCCESS_POPUP_MESSAGE);
                    })
                    .catch((err) => {
                        const msg =
                            (err && err.data && err.data.message) ||
                            (err && err.message) ||
                            "알람 테스트에 실패했습니다.";
                        this.alertWarning(msg);
                    });
            });
        },
    },
};
</script>

<style scoped>
.detail-notification-icon {
    padding: 1rem;
    color: #444;
    font-size: 48px;
}

.detail-notification-text {
    font-size: 15px;
    font-weight: bold;
}

.detail-container {
    height: 100%;
    width: 100%;
    padding: 1rem 1rem 1rem 1rem;
    border-radius: 10px;
    overflow: hidden;

    display: flex;
    flex-direction: column;

    font-size: 12px;
}

.detail-placeholder {
    height: 100%;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.detail-main {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.detail-header {
    flex: 0 0 auto;
    margin-bottom: 0px !important;
    padding: 8px 16px 8px 16px;
    overflow: hidden;

    display: flex;
    justify-content: space-between;
    align-items: center;

    border-radius: 6px 6px 0px 0px;
    background-color: #f1f1f1;
    border: solid #ececec 1px;
    border-bottom: 1px solid #ccc;
    font-weight: bold;
}

.detail-header .detail-title {
    padding: 0 !important;

    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    min-width: 0;
}

.detail-content {
    flex: 1;
    min-height: 0;
    width: 100%;

    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;

    margin-top: 2px;
    padding: 24px 20px;
    overflow-x: hidden;
    overflow-y: auto;
    background-color: white;
    border: solid #ececec 1px;
    border-radius: 0px 0px 6px 6px;
}

.detail-control {
    margin-bottom: 8px !important;
    display: flex;
    justify-content: flex-end;
    flex-shrink: 0;
}

.detail-bottom {
    flex: 0 0 auto;
    margin-top: 8px;

    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.detail-bottom-buttons {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    margin-top: 4px;
}

.detail-btn {
    flex: 1;
    margin: 0px 4px 0px 4px;
}

.detail-bottom-info {
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 4px 8px 4px 8px;
}

.title {
    font-weight: 600;
    margin-right: 20px;
}

.badge {
    font-size: 12px;
    font-weight: 500;
    margin-right: 4px;
    display: inline-block;
    min-width: 20px;
    padding: 0px 10px 0px 10px;
    color: #fff;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
    line-height: 22px;
    border-radius: 8px;
}

.badge.title1 {
    background-color: #ff6d00;
    color: #fff;
}

.badge.title2 {
    background-color: #546e7a;
    color: #fff;
}

.badge.others {
    background-color: #cfd8dc;
    color: #000;
}
</style>
